import { consume } from "@lit/context";
import { html, LitElement, PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ProductSelectionContext, productSelectionContextKey } from "../product-selection.context";
import { repeat } from "lit/directives/repeat.js";
import { AvailableProductItem, StagingCartItem, TimeSlotItem } from "../../types";
import { OrderManagementContext, orderManagementContextKey } from "../../order-management/order-management.context";
import { createRef, ref } from "lit/directives/ref.js";
import autoAnimate, { AnimationController } from "@formkit/auto-animate";
import { when } from "lit/directives/when.js";
import { produce } from "immer";
import { Subscription } from "rxjs";

@customElement("product-form")
export class ProductFormElement extends LitElement {

    private rootElement = createRef<HTMLElement>();
    private animationController: AnimationController | null = null;
    private _isLoadingSubscription: Subscription | undefined;

    @consume({ context: productSelectionContextKey})
    private productSelectionContext! : ProductSelectionContext;

    @consume({ context: orderManagementContextKey, subscribe: true })
    @state()
    private orderManagementContext: OrderManagementContext | null = null;

    @state()
    private isLoading: boolean = false;

    @state()
    private stagingCartItems: StagingCartItem[] | null = null;

    @state()
    private selectedTimeSlot: TimeSlotItem | null = null;

    @property({ type: Object})
    public product?: AvailableProductItem | null = null;

    public override connectedCallback() {
        super.connectedCallback();

        this.productSelectionContext.selectedTimeSlot$.subscribe({ next: (next) => this.selectedTimeSlot = next });
        this._isLoadingSubscription = this.orderManagementContext?.loadingChanged$.subscribe((next) => {
            console.info("ProductFormElement received loading state change:", next);
            this.isLoading = next;
        });
    }

    protected override firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);
        if(this.rootElement.value && !this.animationController) {
            this.animationController = autoAnimate(this.rootElement.value);
        }
    }

    protected override updated(_changedProperties: PropertyValues): void {
        super.updated(_changedProperties);

        console.debug("ProductFormElement updated with changed properties:", _changedProperties);

        if(this.product == null) {
            console.warn("ProductFormElement rendered without a product, this component requires a selected product to function correctly");
            return;
        }

        if(!this.orderManagementContext || !this.selectedTimeSlot) {
            return;
        }


        if(!_changedProperties.has("stagingCartItems")) {
            this.stagingCartItems = this.selectedTimeSlot.availableProductVariants?.map(variant => {

                const orderLine = this.orderManagementContext!.getOrderLine(this.selectedTimeSlot!.startDate, this.selectedTimeSlot!.endDate, variant.id!);

                return {
                    orderLineId: orderLine?.id,
                    productVariant: variant,
                    date: this.selectedTimeSlot!.startDate,
                    timeSlotId: this.selectedTimeSlot!.timeSlotId!,
                    quantity: orderLine?.quantity ?? variant.minimumQtyPerSession ?? 0
                }
            }) ?? [];
        }


        if(_changedProperties.has("_orderManagementContext")) {

            if(this._isLoadingSubscription) {
                this._isLoadingSubscription.unsubscribe();
            }

            this._isLoadingSubscription = this.orderManagementContext?.loadingChanged$.subscribe((next) => {
                console.info("ProductFormElement received loading state change:", next);
                this.isLoading = next;
            });
        }
    }

    private handleQuantityInput(event: Event & { target: HTMLInputElement }, item: StagingCartItem) {
        const newQuantity = parseInt(event.target.value, 10);
        if (!isNaN(newQuantity)) {
            this.updateQuantity(item, newQuantity);
        }
    }

    private incrementQuantity(item: StagingCartItem) {
        this.updateQuantity(item, item.quantity + 1);
    }
    private decrementQuantity(item: StagingCartItem) {
        this.updateQuantity(item, item.quantity - 1);
    }

    private updateQuantity(item: StagingCartItem, newQuantity: number) {
        const totalBooked = this.stagingCartItems!.reduce((total, item) => total + (item.productVariant.isSpectatorProduct ? 0 : item.quantity), 0);
        const maximumSessionQty = this.selectedTimeSlot?.availableCapacity ?? 0;
        const maxProductVariantQty = item.productVariant.maximumQtyPerSession ?? maximumSessionQty;
        const minOrderQuantity = item.productVariant.minimumQtyPerSession ?? 0;
        const requestedSessionQty = totalBooked - item.quantity + newQuantity;

        if(minOrderQuantity > newQuantity) {
            newQuantity = minOrderQuantity;
        }

        if(newQuantity >= maxProductVariantQty) {
            newQuantity = maxProductVariantQty;
        }

        if(requestedSessionQty >= maximumSessionQty && item.productVariant.isSpectatorProduct === false) {
            newQuantity = maximumSessionQty - (totalBooked - item.quantity);
        }

        this.stagingCartItems = produce(this.stagingCartItems!, draft => {
            const index = draft.findIndex(cartItem => cartItem.productVariant.id === item.productVariant.id);
            if (index !== -1) {
                draft[index].quantity = newQuantity;
            }
        });
    }

    private addToOrder() {
        if(!this.orderManagementContext) {
            return;
        }

        this.orderManagementContext.addOrUpdateOrderLines(this.stagingCartItems ?? []);
    }

    protected override render() {
        return html`
            <div class="flex flex-col gap-1" ${ref(this.rootElement)}>
                ${when(this.selectedTimeSlot, () => html`
                    <div class="font-bold">Available Products</div>
                    ${repeat(this.stagingCartItems ?? [], item => {
                        const min = item.productVariant.minimumQtyPerSession ?? 0;

                        return html`
                            <div class="flex items-center gap-4">
                                <div class="grow">${item.productVariant.name}</div>
                                <div>£${item.productVariant.price?.toFixed(2)}</div>
                                <div class="outline-mcr-purple grid grid-cols-[auto_min-content_auto] rounded border border-black outline-offset-1 focus-within:outline-1">
                                    <button class="w-8 cursor-pointer rounded text-center text-lg leading-8 hover:bg-gray-100" @click=${() => this.decrementQuantity(item)}>-</button>
                                    <input
                                        type="number"
                                        min="${min}"
                                        .value="${item.quantity.toString()}"
                                        @change=${(event: Event & { target: HTMLInputElement }) => this.handleQuantityInput(event, item)}
                                        class="spinners-none w-[calc(3ch+var(--spacing)*2)] p-2 focus:outline-none" />
                                    <button class="w-8 cursor-pointer rounded text-center text-lg leading-8 hover:bg-gray-100" @click=${() => this.incrementQuantity(item)}>+</button>
                                </div>
                            </div>
                        `;
                    })}
                    <loading-button ?loading="${this.isLoading ?? false}" ?disabled="${this.isLoading ?? false}" buttonText="Add to Basket" @click=${() => this.addToOrder()}></loading-button>
                    <!-- <button class="mt-4 cursor-pointer self-end rounded transition bg-mcr-purple px-4 py-2 text-white hover:bg-mcr-purple-700" @click=${() => this.addToOrder()}>Add to Basket</button> -->
                `)}
            </div>
        `;
    }

    protected override createRenderRoot() {
        return this;
    }
}